/**
 * The app's Content-Security-Policy, in two variants: the application's, and
 * the public marketing pages', which alone admits the tag manager.
 *
 * This has to be owned by the app rather than the web server. The shared host
 * supplies a generic CSP only when the proxied response carries none of its own
 * (`/etc/nginx/conf.d/csp-map.conf`), and that fallback was written for the
 * other sites on the box: it allows Stripe and a handful of CDNs, and its
 * `connect-src` names no object storage at all. Because the app sent a CSP on
 * `/oauth/consent` and nowhere else, every other route inherited that fallback
 * and the browser refused the presigned `PUT` to S3 before the request left the
 * page — surfacing only as `TypeError: Failed to fetch`. Sending a complete
 * policy from here suppresses the fallback entirely, so the two never stack
 * into an intersection.
 *
 * Keep the allowances tight: the bucket is one of the few external origins the
 * dashboard genuinely needs, and it is also a host an XSS would try to reach.
 */

export interface CspEnvironment {
  /** Origin of the bucket presigned upload/download URLs point at. */
  storageOrigin?: string;
  /** Origin the browser calls for the API; same-origin in every deployment so far. */
  apiBaseUrl?: string;
  /** GlitchTip/Sentry DSN — crash reports POST to its origin. */
  sentryDsn?: string;
  /** `next dev` needs eval for React Refresh and a websocket for HMR. */
  development?: boolean;
  /**
   * Admit the institution's tag manager. Set only for the public marketing
   * pages (see `lib/publicSite.ts`); the application's policy never has it.
   */
  marketingTags?: boolean;
}

// reCAPTCHA v3 is used by the public contact and immersive-course forms. Keep
// these path-scoped sources aligned with Google's documented CSP allowlist;
// removing them makes both forms fail closed before they can be submitted.
const RECAPTCHA_SCRIPT_SRC = [
  'https://www.google.com/recaptcha/',
  'https://www.gstatic.com/recaptcha/',
];
const RECAPTCHA_CONNECT_SRC = ['https://www.google.com/recaptcha/'];
const RECAPTCHA_FRAME_SRC = [
  'https://www.google.com/recaptcha/',
  'https://recaptcha.google.com/recaptcha/',
];

// The public site's tag manager container (GTM-N79R44W9) loads exactly two
// things: the Google tag for Analytics (G-R0H1J68HCD) and a Meta Pixel in a
// Custom HTML tag. These are those vendors' documented CSP sources. A tag
// added to the container later that needs another origin is blocked until it
// is added here — deliberately: the container is editable from outside this
// repository, and this list is the review point for what it may load.
const MARKETING_SCRIPT_SRC = ['https://*.googletagmanager.com', 'https://connect.facebook.net'];
const MARKETING_CONNECT_SRC = [
  'https://*.google-analytics.com',
  'https://*.analytics.google.com',
  'https://*.googletagmanager.com',
  'https://www.facebook.com',
  'https://connect.facebook.net',
];
const MARKETING_IMG_SRC = [
  'https://*.google-analytics.com',
  'https://*.googletagmanager.com',
  'https://www.facebook.com',
];

/**
 * Reduce a configured URL to its origin. Returns null for anything unusable so
 * a blank or malformed env value drops the source instead of emitting a broken
 * directive (a CSP with a garbage token is ignored wholesale by some browsers).
 */
export function normalizeHttpOrigin(value: string | undefined): string | null {
  const trimmed = value?.trim();
  if (!trimmed) return null;
  try {
    const parsed = new URL(trimmed);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null;
    const { origin } = parsed;
    return origin && origin !== 'null' ? origin : null;
  } catch {
    return null;
  }
}

function sources(values: Array<string | null | undefined>): string {
  return [...new Set(values.filter((value): value is string => Boolean(value)))].join(' ');
}

export function buildContentSecurityPolicy(env: CspEnvironment = {}): string {
  const storage = normalizeHttpOrigin(env.storageOrigin);
  const api = normalizeHttpOrigin(env.apiBaseUrl);
  const telemetry = normalizeHttpOrigin(env.sentryDsn);
  const dev = env.development === true;
  const tags = env.marketingTags === true;

  const directives: Array<[string, Array<string | null>]> = [
    ['default-src', ["'self'"]],
    ['base-uri', ["'self'"]],
    // Certificate artwork is fetched through the authenticated API, validated
    // by the backend renderer, and then previewed from a local blob URL. Do not
    // allow remote or bucket-hosted object embeds.
    ['object-src', ['blob:']],
    ['frame-ancestors', ["'none'"]],
    ['form-action', ["'self'"]],
    // Next streams hydration payloads through inline <script> tags. Replacing
    // 'unsafe-inline' with a nonce means generating one per request in
    // middleware on every route; until that exists this matches what the app
    // has always run under.
    [
      'script-src',
      [
        "'self'",
        "'unsafe-inline'",
        ...(dev ? ["'unsafe-eval'"] : []),
        ...RECAPTCHA_SCRIPT_SRC,
        ...(tags ? MARKETING_SCRIPT_SRC : []),
      ],
    ],
    // React uses listeners rather than inline event-handler attributes. This
    // preserves Next's required inline script blocks without permitting
    // onclick= style execution in injected markup.
    ['script-src-attr', ["'none'"]],
    ['style-src', ["'self'", "'unsafe-inline'"]],
    ['font-src', ["'self'", 'data:']],
    ['img-src', ["'self'", 'data:', 'blob:', storage, ...(tags ? MARKETING_IMG_SRC : [])]],
    // Lesson audio and video stream straight from presigned bucket URLs.
    ['media-src', ["'self'", 'blob:', storage]],
    [
      'connect-src',
      [
        "'self'",
        api,
        // Presigned uploads PUT here; without it the browser blocks every upload.
        storage,
        telemetry,
        ...RECAPTCHA_CONNECT_SRC,
        ...(tags ? MARKETING_CONNECT_SRC : []),
        ...(dev ? ['ws:'] : []),
      ],
    ],
    ['worker-src', ["'self'", 'blob:']],
    ['frame-src', ["'self'", ...RECAPTCHA_FRAME_SRC]],
  ];

  return directives.map(([name, values]) => `${name} ${sources(values)}`).join('; ');
}
